import { DateFilterOption } from "@/design-system/components/DateFilter/DateFilter";

export type QuestionBase = {
  id: string;
  text: string;
  position: number;
};

export type SingleChoiceQuestion = QuestionBase & {
  type: "single_choice";
  options: string[];
};

export type OpenTextQuestion = QuestionBase & {
  type: "open_text";
  placeholder: string;
};

export type YesNoQuestion = QuestionBase & {
  type: "yes_no";
};

export type RatingQuestion = QuestionBase & {
  type: "rating";
  scale: 5; // Fixed at 1-5 for MVP. PRD §6.3 specifies rating scale.
  lowLabel: string;
  highLabel: string;
};

export type NPSQuestion = QuestionBase & {
  type: "nps";
};

export type DistributionChartResult = Extract<
  QuestionResponseSummary,
  { questionType: "single_choice" | "yes_no" | "rating" }
>;
export type NpsChartResult = Extract<
  QuestionResponseSummary,
  { questionType: "nps" }
>;
export type OpenTextResultQuestion = Extract<
  QuestionResponseSummary,
  { questionType: "open_text" }
>;

export type DistributionEntry = { label: string; count: number };

export type NpsGrouping = {
  totalRespondents: number;
  pctRespondents: number | null;
};

export type NpsData = {
  score: number;
  promoters: NpsGrouping;
  demoters: NpsGrouping;
  passives: NpsGrouping;
  distribution: DistributionEntry[];
};

export type RatingData = {
  score: number;
  distribution: DistributionEntry[];
};

export type OpenTextData = {
  samples: string[];
};

export type DistributionData = {
  distribution: DistributionEntry[];
};

export type Question =
  | SingleChoiceQuestion
  | OpenTextQuestion
  | YesNoQuestion
  | RatingQuestion
  | NPSQuestion;

export type QuestionType = Question["type"];

// Human-readable labels for question types
export const QUESTION_TYPE_LABELS: Record<QuestionType, string> = {
  single_choice: "Single Choice",
  open_text: "Open Text",
  yes_no: "Yes / No",
  rating: "Rating Scale",
  nps: "NPS (Net Promoter Score)",
};

export type UrlMatchType = "contains" | "exact" | "starts_with" | "wildcard";

export type UrlMatchCondition = {
  pattern: string;
  matchType: UrlMatchType;
};

export type SurveyStatus = "draft" | "active" | "paused";

export type SurveyPosition =
  | "bottom_right"
  | "bottom_left"
  | "top_right"
  | "top_left";

export type SurveyTheme = {
  buttonColor: string;
  backgroundColor: string;
  textColor: string;
};

export type SurveyAppearance = {
  position: SurveyPosition;
  theme: SurveyTheme;
};

export const DEFAULT_SURVEY_APPEARANCE: SurveyAppearance = {
  position: "bottom_right",
  theme: {
    buttonColor: "#3F51B5",
    backgroundColor: "#FFFFFF",
    textColor: "#1E2125",
  },
};

// Human-readable labels for survey positions (builder UI).
export const SURVEY_POSITION_LABELS: Record<SurveyPosition, string> = {
  bottom_right: "Bottom right",
  bottom_left: "Bottom left",
  top_right: "Top right",
  top_left: "Top left",
};

export type Survey = {
  id: string;
  name: string;
  description: string;
  status: SurveyStatus;
  questions: Question[];
  urlMatchCondition: UrlMatchCondition;
  idleTimeSeconds: number;
  startDate: Date | null;
  endDate: Date | null;
  // Optional — only set when the user picks the "complete after N responses"
  // limit mode in the builder. TODO(CE-1283): backend persistence.
  responseLimit?: number | null;
  // Customizes where/how the survey widget renders at runtime
  // (position + theme colors). Persisted by the backend.
  appearance: SurveyAppearance;
  responseCount: number;
  createdAt: Date;
  updatedAt: Date;
  sourceConversationId: string | null;
};

type QuestionResponseSummaryBase = {
  questionId: string;
  questionIndex: number;
  questionPrompt: string;
  totalResponses: number;
};

export type QuestionResponseSummary =
  | (QuestionResponseSummaryBase & {
      questionType: "single_choice" | "yes_no";
      data: DistributionData;
    })
  | (QuestionResponseSummaryBase & {
      questionType: "rating";
      data: RatingData;
    })
  | (QuestionResponseSummaryBase & {
      questionType: "open_text";
      data: OpenTextData;
    })
  | (QuestionResponseSummaryBase & {
      questionType: "nps";
      data: NpsData;
    });

export type SurveyResultsData = {
  surveyId: string;
  // Total sessions where the survey was shown ($survey_shown).
  impressions: number;
  // Sessions that reached completion ($survey_completed).
  responses: number;
  // responses / impressions, or null when impressions == 0.
  responseRate: number | null;
  questionSummaries: QuestionResponseSummary[];
};

export type SurveyTimeseriesGranularity = "hour" | "day" | "week";

export type SurveyTimeseriesPoint = {
  bucket: Date;
  impressions: number;
  responses: number;
};

export type SurveyTimeseries = {
  granularity: SurveyTimeseriesGranularity;
  points: SurveyTimeseriesPoint[];
};

// One row per session in the Responses tab. Answers is a map keyed by
// question index (as a string, since JSON object keys are strings).
export type SurveyResponseRow = {
  deviceId: string;
  userId: string | null;
  startedAt: Date;
  completedAt: Date | null;
  pageUrl: string | null;
  answers: Record<string, string>;
};

export type SurveyResponsesPage = {
  rows: SurveyResponseRow[];
  total: number;
};

export type SurveyDateRange = {
  from: Date;
  to: Date;
};

export type ValidationError = {
  field: string;
  message: string;
};

export function validateSurveyForPublish(survey: Survey): ValidationError[] {
  const errors: ValidationError[] = [];

  if (!survey.name.trim()) {
    errors.push({ field: "name", message: "Survey name is required" });
  }

  if (survey.questions.length === 0) {
    errors.push({
      field: "questions",
      message: "At least one question is required",
    });
  }

  survey.questions.forEach((q, i) => {
    if (!q.text.trim()) {
      errors.push({
        field: `questions[${i}].text`,
        message: `Question ${i + 1} text is required`,
      });
    }
    if (q.type === "single_choice" && q.options.length < 2) {
      errors.push({
        field: `questions[${i}].options`,
        message: `Question ${i + 1} needs at least 2 options`,
      });
    }
  });

  if (!survey.urlMatchCondition.pattern && survey.idleTimeSeconds <= 0) {
    errors.push({
      field: "displayConditions",
      message:
        "At least one display condition is required (URL match or idle time)",
    });
  }

  if (survey.startDate && survey.endDate && survey.endDate < survey.startDate) {
    errors.push({
      field: "endDate",
      message: "End date must be after start date",
    });
  }

  return errors;
}

export const SURVEY_DATE_FILTERS: DateFilterOption[] = [
  { type: "today" as const, label: "Today", tooltip: "Today only" },
  { type: "7DaysAgo" as const, label: "7D", tooltip: "Last 7 complete days" },
  {
    type: "30DaysAgo" as const,
    label: "30D",
    tooltip: "Last 30 complete days",
  },
  {
    type: "3MonthsAgo" as const,
    label: "3M",
    tooltip: "Last 3 complete months",
  },
  {
    type: "6MonthsAgo" as const,
    label: "6M",
    tooltip: "Last 6 complete months",
  },
  {
    type: "12MonthsAgo" as const,
    label: "12M",
    tooltip: "Last 12 complete months",
  },
];
